import {inject, injectable} from 'inversify'
import {DI} from '@/src/config/di/types'
import type {IBasketRepository} from '@/src/core/domain/repositories'
import {BasketError} from '@/src/core/domain/errors'
import {OperationResult, Result} from '@/src/shared/utils/result'
import {BasketData, NewBasketItem} from '@/src/entities/basket/types'
import {Product, ProductVariant} from '@/src/core/domain/entities/product'
import type {IErrorReporter} from '@/src/ports/services/monitoring'

/**
 * Add to Basket Use Case
 *
 * Orchestrates adding a product to the basket.
 * Follows Single Responsibility Principle - only handles the add operation.
 *
 * @example
 * const result = await addToBasketUseCase.execute(product, variant)
 * if (result.isOk()) {
 *   // Update UI with new basket data
 * } else {
 *   // Handle error
 * }
 */
@injectable()
export class AddToBasketUseCase {
  @inject(DI.IBasketRepository)
  basketRepository!: IBasketRepository

  @inject(DI.IErrorReporter)
  errorReporter!: IErrorReporter

  async execute(
    product: Product,
    variant?: ProductVariant,
    quantity: number = 1,
  ): Promise<OperationResult<BasketData, BasketError>> {
    const item: NewBasketItem = {
      product_id: product.id,
      quantity,
      variant_id: variant?.id,
    }

    try {
      const basketData = await this.basketRepository.add(item)
      return Result.ok(basketData)
    } catch (error) {
      this.errorReporter.capture(
        error instanceof Error ? error : new Error(String(error)),
        {product_id: product.id, variant_id: variant?.id},
      )
      return Result.fail(new BasketError('ADD_FAILED', error))
    }
  }
}
